import { ComponentProps, ReactNode } from 'react';

import { twMerge } from 'tailwind-merge';

import { Button } from '../Button';
import { Icon, IconNames } from '../Icon';
import { Text } from '../Text';

const VARIANT_STYLE = {
  primary: {
    bg: 'bg-light-blue-50',
    icon: 'info',
    iconColor: 'blue',
  },
  secondary: {
    bg: 'bg-neutral-100',
    icon: 'info',
    iconColor: 'neutral',
  },
  success: {
    bg: 'bg-green-50',
    icon: 'correct',
    iconColor: 'green',
  },
  warning: {
    bg: 'bg-yellow-50',
    icon: 'error',
    iconColor: 'yellow',
  },
  danger: {
    bg: 'bg-red-50',
    icon: 'wrong',
    iconColor: 'red',
  },
} satisfies Record<
  string,
  {
    bg: string;
    icon: IconNames;
    iconColor: ComponentProps<typeof Icon>['color'];
  }
>;

const SIZE_STYLE = {
  md: {
    title: 'subtitle-1',
    body: 'body-1',
  },
  lg: {
    title: 'subtitle-2',
    body: 'body-2',
  },
} satisfies Record<
  string,
  {
    title: ComponentProps<typeof Text>['variant'];
    body: ComponentProps<typeof Text>['variant'];
  }
>;

interface InformationBoxProps {
  readonly title?: string;
  readonly variant?: keyof typeof VARIANT_STYLE;
  readonly size?: keyof typeof SIZE_STYLE;
  readonly icon?: true | IconNames;
  readonly children?: ReactNode;
  readonly className?: string;
  readonly onClick?: () => void;
}

function InformationBox({ title, variant = 'primary', size = 'md', icon, children, className, onClick }: InformationBoxProps) {
  const variantStyle = VARIANT_STYLE[variant];
  const sizeStyle = SIZE_STYLE[size];

  return (
    <div role="alert" data-testid="info-box" className={twMerge('flex gap-2 rounded-md p-4', variantStyle.bg, className)}>
      {icon && (
        <div className={twMerge('flex shrink-0', !children && 'items-center')}>
          <Icon icon={typeof icon === 'string' ? icon : variantStyle.icon} color={variantStyle.iconColor} size={size} />
        </div>
      )}
      <div className="flex w-full flex-col gap-1">
        {title && (
          <Text variant={sizeStyle.title} weight="semibold" color="primary" className="font-semibold text-neutral-900">
            {title}
          </Text>
        )}
        {children && (
          <Text variant={sizeStyle.body} className="text-neutral-700">
            {children}
          </Text>
        )}
      </div>
      {onClick ? (
        <Button variant="nude" size="none" className="rounded-md p-0.5" onClick={onClick}>
          <Icon icon="close" size="md" />
        </Button>
      ) : null}
    </div>
  );
}

export { InformationBox };
